import { authenticatedFetch } from '../utils/fetchUtils';
import { getFingerprint } from '../utils/fingerprintUtils';

const BASE_URL = (process.env.REACT_APP_API_BASE_URL || window.location.origin) + "/api/auth";

export const signInUser = async (email, password) => {
  try {
    const response = await fetch(`${BASE_URL}/signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Login failed');
    }

    return await response.json(); // Should return { token, user }
  } catch(err) {
    console.error('[signInUser] network error', err);
    throw err;
  }

};

// formData shape:
//   status: 'active' | 'temp'   (default 'active' if omitted for backward compat)
//   Normal signup:       { status: 'active', name, email, password, birthDetails, ... }
//   Temp creation:       { status: 'temp', name, birthDetails, precomputedChart, ... }
//   Temp→active convert: { status: 'active', email, password, ... }  — backend looks up
//                         the temp row by fingerprint and mutates it in place.
// Fingerprint is always attached.
export const signUpUser = async (formData) => {
  const fingerprint = await getFingerprint();
  const payload = {
    status: 'active',
    ...formData,
    fingerprint,
  };

  const response = await fetch(`${BASE_URL}/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok) {
    // 409 with existingAccount: true — fingerprint matches a live active user.
    // Frontend should redirect to sign-in. Throw a typed error so callers can branch.
    if (response.status === 409 && data.existingAccount) {
      const err = new Error(data.message || 'An account already exists for this device.');
      err.existingAccount = true;
      err.maskedEmail = data.email;
      throw err;
    }
    throw new Error(data.message || 'Signup failed');
  }
  return data; // { token, user } OR { existingAccount: true, email } (2xx — shouldn't happen now but keep for safety)
};

export const getProfile = async () => {
  const response = await authenticatedFetch(`${BASE_URL}/profile`);

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch profile');
  }

  return await response.json();
};

export const updateProfile = async (data) => {
  const response = await authenticatedFetch(`${BASE_URL}/profile`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update profile');
  }

  return await response.json(); // Returns { user, token }
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await authenticatedFetch(`${BASE_URL}/password`, {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to change password');
  }

  return await response.json();
};

export const forgotPassword = async (email) => {
  const response = await fetch(`${BASE_URL}/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Request failed');
  }

  return await response.json();
};

export const resetPassword = async (token, newPassword) => {
  const response = await fetch(`${BASE_URL}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Reset failed');
  }

  return await response.json();
};

export const getChartPreview = async (birthDetails, name) => {
  const country = localStorage.getItem('countryOverride');
  const response = await fetch(`${BASE_URL}/chart-preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(country && { 'X-User-Country': country }),
    },
    body: JSON.stringify({ birthDetails, name }),
  });

  if (!response.ok) return null;
  return response.json();
};

export const signInWithGoogle = async (idToken) => {
  const response = await fetch(`${BASE_URL}/google/signin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Google sign-in failed');
  }

  return await response.json(); // { token, user } OR { needsSignup, googleProfile }
};

export const signUpWithGoogle = async (payload) => {
  const fingerprint = await getFingerprint();
  const response = await fetch(`${BASE_URL}/google/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, fingerprint }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Google sign-up failed');
  }

  return await response.json(); // { token, user }
};

export const verifyEmail = async (token) => {
  const response = await fetch(`${BASE_URL}/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Verification failed');
  return data;
};

export const resendVerification = async () => {
  const response = await authenticatedFetch(`${BASE_URL}/resend-verification`, {
    method: 'POST',
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to resend verification email');
  return data;
};

export const updatePendingEmail = async (email) => {
  const response = await authenticatedFetch(`${BASE_URL}/pending-email`, {
    method: 'PUT',
    body: JSON.stringify({ email }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to update email');
  return data;
};

export const deleteAccount = async ({ password, idToken } = {}) => {
  const response = await authenticatedFetch(`${BASE_URL}/account`, {
    method: 'DELETE',
    body: JSON.stringify({ password, idToken }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete account');
  }

  return await response.json();
};
